import React, { useState } from 'react'
import styles from './CollapsiblePanel.module.scss'
import { ChevronUp, ChevronDown } from './Icons'

export type PanelItem = {
  /** 面板唯一标识 */
  key: string | number
  /** 面板标题 */
  title: React.ReactNode
  /** 面板内容 */
  children?: React.ReactNode
  /** 该面板是否可展开/收起，默认为true */
  expandable?: boolean
  /** 默认是否展开 */
  defaultExpanded?: boolean
  /** 自定义标题样式 */
  titleClassName?: string
  /** 自定义内容样式 */
  contentClassName?: string
  /** 自定义面板样式 */
  className?: string
  /** 图标位置 - 'left' 或 'right' */
  iconPosition?: 'left' | 'right'
}

export type CollapsiblePanelProps = {
  /** 面板项数组（提供时，组件将渲染为面板组） */
  items?: PanelItem[]
  /** 是否启用手风琴模式（同时只能展开一个面板） */
  accordion?: boolean

  // 单个面板属性（当不提供items时使用）
  /** 面板标题 */
  title?: React.ReactNode
  /** 面板内容 */
  children?: React.ReactNode
  /** 默认是否展开 */
  defaultExpanded?: boolean
  /** 该面板是否可展开/收起，默认为true */
  expandable?: boolean
  /** 自定义标题样式 */
  titleClassName?: string
  /** 自定义内容样式 */
  contentClassName?: string
  /** 自定义面板样式 */
  className?: string
  /** 点击展开/收起回调 */
  onToggle?: (expanded: boolean) => void
  /** 面板组中任意面板切换状态时的回调 */
  onPanelToggle?: (key: string | number, expanded: boolean) => void
  /** 图标位置 - 'left' 或 'right' */
  iconPosition?: 'left' | 'right'
}

const CollapsiblePanel: React.FC<CollapsiblePanelProps> = (props) => {
  const {
    items,
    accordion = false,
    title,
    children,
    defaultExpanded = false,
    expandable = true,
    titleClassName = '',
    contentClassName = '',
    className = '',
    onToggle,
    onPanelToggle,
    iconPosition = 'right',
  } = props

  const [expandedPanels, setExpandedPanels] = useState<(string | number)[]>(() => {
    if (items && items.length > 0) {
      // 初始化默认展开的面板
      return items.filter((item) => item.defaultExpanded && item.expandable !== false).map((item) => item.key)
    }
    return []
  })
  const [expanded, setExpanded] = useState(defaultExpanded)

  const handleToggle = (key: string | number, expanded: boolean) => {
    setExpandedPanels((prevExpanded) => {
      if (expanded) {
        // 如果是展开，手风琴模式下替换整个数组
        // 否则将此key添加到展开列表中
        return accordion ? [key] : [...prevExpanded, key]
      } else {
        // 如果是收起，从展开列表中移除
        return prevExpanded.filter((panelKey) => panelKey !== key)
      }
    })
    onPanelToggle?.(key, expanded)
  }

  const handleSingleToggle = () => {
    if (!expandable) return
    const newExpanded = !expanded
    setExpanded(newExpanded)
    onToggle?.(newExpanded)
  }

  // 如果提供了items，则渲染为面板组
  if (items && items.length > 0) {
    return (
      <div className={className}>
        {items.map((item) => {
          const isExpanded = expandedPanels.includes(item.key)
          const isExpandable = item.expandable !== false
          const ChevronIcon = isExpanded ? ChevronUp : ChevronDown

          return (
            <div key={item.key} className={`${styles.panel} ${item.className || ''}`}>
              <div
                className={`${styles.header} ${item.titleClassName || ''}`}
                onClick={() => {
                  if (isExpandable) {
                    handleToggle(item.key, !isExpanded)
                  }
                }}
                style={{ cursor: isExpandable ? 'pointer' : 'default' }}
              >
                {item.iconPosition === 'left' && isExpandable && <ChevronIcon className={styles.icon} />}
                <div className={styles.title}>{item.title}</div>
                {item.iconPosition !== 'left' && isExpandable && <ChevronIcon className={styles.icon} />}
              </div>
              <div
                className={`${styles.content} ${isExpanded ? styles.contentExpanded : ''} ${item.contentClassName || ''}`}
              >
                <div className={styles.contentInner}>{item.children}</div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // 否则渲染为单个面板
  const ChevronIcon = expanded ? ChevronUp : ChevronDown

  return (
    <div className={`${styles.panel} ${className}`}>
      <div
        className={`${styles.header} ${titleClassName}`}
        onClick={handleSingleToggle}
        style={{ cursor: expandable ? 'pointer' : 'default' }}
      >
        {iconPosition === 'left' && expandable && <ChevronIcon className={styles.icon} />}
        <div className={styles.title}>{title}</div>
        {iconPosition === 'right' && expandable && <ChevronIcon className={styles.icon} />}
      </div>
      <div className={`${styles.content} ${expanded ? styles.contentExpanded : ''} ${contentClassName}`}>
        <div className={styles.contentInner}>{children}</div>
      </div>
    </div>
  )
}

export default CollapsiblePanel
